'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

interface RgbColor {
    r: number;
    g: number;
    b: number;
}

interface ColorPickerProps {
    value?: RgbColor;
    onChange: (color: RgbColor) => void;
    trigger?: React.ReactElement;
    className?: string;
}

function hsvToRgb(h: number, s: number, v: number): RgbColor {
    const i = Math.floor(h / 60) % 6;
    const f = h / 60 - Math.floor(h / 60);
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    let r = 0,
        g = 0,
        b = 0;
    switch (i) {
        case 0:
            r = v;
            g = t;
            b = p;
            break;
        case 1:
            r = q;
            g = v;
            b = p;
            break;
        case 2:
            r = p;
            g = v;
            b = t;
            break;
        case 3:
            r = p;
            g = q;
            b = v;
            break;
        case 4:
            r = t;
            g = p;
            b = v;
            break;
        case 5:
            r = v;
            g = p;
            b = q;
            break;
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
    const rr = r / 255,
        gg = g / 255,
        bb = b / 255;
    const max = Math.max(rr, gg, bb),
        min = Math.min(rr, gg, bb);
    const d = max - min;
    let h = 0;
    const s = max === 0 ? 0 : d / max;
    const v = max;
    if (d !== 0) {
        if (max === rr) h = ((gg - bb) / d + 6) % 6;
        else if (max === gg) h = (bb - rr) / d + 2;
        else h = (rr - gg) / d + 4;
        h *= 60;
    }
    return { h, s, v };
}

function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    const rr = r / 255,
        gg = g / 255,
        bb = b / 255;
    const max = Math.max(rr, gg, bb),
        min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === rr) h = ((gg - bb) / d + 6) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    return { h: Math.round(h * 60), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function parseHex(hex: string): RgbColor | null {
    const clean = hex.replace('#', '');
    if (clean.length === 6) {
        const r = parseInt(clean.slice(0, 2), 16);
        const g = parseInt(clean.slice(2, 4), 16);
        const b = parseInt(clean.slice(4, 6), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) return { r, g, b };
    }
    if (clean.length === 3) {
        const r = parseInt(clean[0] + clean[0], 16);
        const g = parseInt(clean[1] + clean[1], 16);
        const b = parseInt(clean[2] + clean[2], 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) return { r, g, b };
    }
    return null;
}

export default function ColorPicker({
    value = { r: 255, g: 0, b: 0 },
    onChange,
    trigger,
    className,
}: ColorPickerProps) {
    const [open, setOpen] = useState(false);
    const [popoverDir, setPopoverDir] = useState<'top' | 'bottom'>('bottom');
    const [hsv, setHsv] = useState(() => rgbToHsv(value.r, value.g, value.b));
    const [hexInput, setHexInput] = useState(() => rgbToHex(value.r, value.g, value.b));

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const openRef = useRef(false);

    useEffect(() => {
        openRef.current = open;
    }, [open]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!openRef.current) return;
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        setHsv(rgbToHsv(value.r, value.g, value.b));
        setHexInput(rgbToHex(value.r, value.g, value.b));
    }, [value.r, value.g, value.b]);

    const drawPanel = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width;
        const h = canvas.height;
        const hueColor = `hsl(${hsv.h}, 100%, 50%)`;
        const gradH = ctx.createLinearGradient(0, 0, w, 0);
        gradH.addColorStop(0, 'white');
        gradH.addColorStop(1, hueColor);
        ctx.fillStyle = gradH;
        ctx.fillRect(0, 0, w, h);
        const gradV = ctx.createLinearGradient(0, 0, 0, h);
        gradV.addColorStop(0, 'transparent');
        gradV.addColorStop(1, 'black');
        ctx.fillStyle = gradV;
        ctx.fillRect(0, 0, w, h);
    }, [hsv.h]);

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                const canvas = canvasRef.current;
                const panel = panelRef.current;
                if (canvas && panel) {
                    canvas.width = panel.offsetWidth;
                    canvas.height = panel.offsetHeight;
                }
                drawPanel();
            });
        }
    }, [open, drawPanel]);

    useEffect(() => {
        drawPanel();
    }, [drawPanel]);

    const emitChange = useCallback(
        (h: number, s: number, v: number) => {
            onChange(hsvToRgb(h, s, v));
        },
        [onChange],
    );

    const onPanelMove = useCallback(
        (e: React.MouseEvent | MouseEvent) => {
            const panel = panelRef.current;
            if (!panel) return;
            const rect = panel.getBoundingClientRect();
            const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
            const newHsv = { h: hsv.h, s: x, v: 1 - y };
            setHsv(newHsv);
            emitChange(newHsv.h, newHsv.s, newHsv.v);
        },
        [hsv.h, emitChange],
    );

    const onPanelMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onPanelMove(e);
            const move = (ev: MouseEvent) => onPanelMove(ev);
            const up = () => {
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
            };
            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        },
        [onPanelMove],
    );

    const onHueMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            const sliderEl = e.currentTarget as HTMLElement;
            const doMove = (ev: MouseEvent) => {
                const rect = sliderEl.getBoundingClientRect();
                const x = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
                const newHsv = { ...hsv, h: x * 360 };
                setHsv(newHsv);
                emitChange(newHsv.h, newHsv.s, newHsv.v);
                requestAnimationFrame(() => drawPanel());
            };
            doMove(e.nativeEvent);
            const up = () => {
                window.removeEventListener('mousemove', doMove);
                window.removeEventListener('mouseup', up);
            };
            window.addEventListener('mousemove', doMove);
            window.addEventListener('mouseup', up);
        },
        [hsv, emitChange, drawPanel],
    );

    const onRgbChange = (channel: 'r' | 'g' | 'b', raw: string) => {
        const num = Math.max(0, Math.min(255, parseInt(raw) || 0));
        const next = { ...value, [channel]: num };
        const newHsv = rgbToHsv(next.r, next.g, next.b);
        setHsv(newHsv);
        setHexInput(rgbToHex(next.r, next.g, next.b));
        onChange(next);
        requestAnimationFrame(() => drawPanel());
    };

    const onHexChange = (raw: string) => {
        setHexInput(raw);
        const parsed = parseHex(raw);
        if (parsed) {
            const newHsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
            setHsv(newHsv);
            onChange(parsed);
            requestAnimationFrame(() => drawPanel());
        }
    };

    const hex = rgbToHex(value.r, value.g, value.b);

    const handleTriggerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const scrollParent = (e.currentTarget as HTMLElement).closest('.overflow-y-auto');
        const containerRect = scrollParent?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
        const containerMid = (containerRect.top + containerRect.bottom) / 2;
        setPopoverDir(rect.top > containerMid ? 'top' : 'bottom');
        setOpen((v) => !v);
    };

    return (
        <div className={`relative ${className ?? ''}`} ref={popoverRef}>
            {/* トリガー */}
            {trigger ? (
                <div onClick={handleTriggerClick}>{trigger}</div>
            ) : (
                <button
                    onClick={handleTriggerClick}
                    className="w-8 h-8 rounded-full border-2 border-white/30 hover:border-white/70 transition-colors shadow-md"
                    style={{ backgroundColor: hex }}
                />
            )}

            {/* ポップオーバー */}
            {open && (
                <div
                    className={`absolute z-50 left-0 bg-[#3a3a52] rounded-xl p-3 shadow-2xl ${
                        popoverDir === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}
                    style={{ width: 420 }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="flex gap-3">
                        {/* 左：HSVパネル */}
                        <div
                            ref={panelRef}
                            className="relative rounded-lg overflow-hidden cursor-crosshair flex-shrink-0"
                            style={{ width: 200, height: 200 }}
                            onMouseDown={onPanelMouseDown}
                        >
                            <canvas ref={canvasRef} className="block w-full h-full" />
                            <div
                                className="absolute w-3 h-3 rounded-full border-2 border-white shadow pointer-events-none"
                                style={{
                                    left: `${hsv.s * 100}%`,
                                    top: `${(1 - hsv.v) * 100}%`,
                                    transform: 'translate(-50%, -50%)',
                                }}
                            />
                        </div>

                        {/* 右：プレビュー＋HEX＋RGB＋HSL＋スライダー */}
                        <div className="flex flex-col gap-2 flex-1">
                            {/* プレビュー（小さめ） */}
                            <div
                                className="w-full h-6 rounded-md border border-white/20 flex-shrink-0"
                                style={{ backgroundColor: hex }}
                            />

                            {/* HEX入力 */}
                            <div className="flex flex-col gap-0.5">
                                <input
                                    type="text"
                                    value={hexInput}
                                    onChange={(e) => onHexChange(e.target.value)}
                                    placeholder="#ffffff"
                                    className="w-full bg-[#2a2a3e] border border-[#444] rounded text-white text-center text-xs py-1 outline-none focus:border-[#888] font-mono"
                                />
                                <span className="text-[10px] text-[#888] text-center">HEX</span>
                            </div>

                            {/* RGB表示 */}
                            <div className="flex gap-1.5">
                                {(['r', 'g', 'b'] as const).map((ch) => (
                                    <div key={ch} className="flex flex-col items-center gap-0.5 flex-1">
                                        <input
                                            type="number"
                                            min={0}
                                            max={255}
                                            value={value[ch]}
                                            onChange={(e) => onRgbChange(ch, e.target.value)}
                                            className="w-full bg-[#2a2a3e] border border-[#444] rounded text-white text-center text-xs py-1 outline-none focus:border-[#888]"
                                        />
                                        <span className="text-[10px] text-[#888] uppercase">{ch}</span>
                                    </div>
                                ))}
                            </div>

                            {/* HSL表示 */}
                            {(() => {
                                const hsl = rgbToHsl(value.r, value.g, value.b);
                                return (
                                    <div className="flex gap-1.5">
                                        {[
                                            { label: 'H', val: `${hsl.h}°` },
                                            { label: 'S', val: `${hsl.s}%` },
                                            { label: 'L', val: `${hsl.l}%` },
                                        ].map(({ label, val }) => (
                                            <div key={label} className="flex flex-col items-center gap-0.5 flex-1">
                                                <div className="w-full bg-[#2a2a3e] border border-[#333] rounded text-white/60 text-center text-xs py-1 font-mono">
                                                    {val}
                                                </div>
                                                <span className="text-[10px] text-[#888]">{label}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* ヒュースライダー */}
                            <div className="mt-auto">
                                <div
                                    className="relative h-3 rounded-full cursor-pointer"
                                    style={{
                                        background:
                                            'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                                    }}
                                    onMouseDown={onHueMouseDown}
                                >
                                    <div
                                        className="absolute top-1/2 w-3.5 h-3.5 rounded-full bg-white border border-black/20 shadow pointer-events-none"
                                        style={{ left: `${(hsv.h / 360) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
